import React, { useState, useEffect } from "react";
import { Modal, Button, Upload, Table, Collapse, Select, Tag, Tooltip } from "antd";
import { UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import globalRequest from "../../../utils/global/globalRequest";
import * as Apis from "../../../utils/helper/enum";
import { useDispatch } from "react-redux";
import { changeLoader } from "../../../redux/reducers/loader";
import toast from "react-hot-toast";
import { useTranslation } from "../../../context/TranslationContext"; // ← Added

const BulkUploadModal = ({ isOpen, onClose, reloadPage }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(); // ← Added

  const [fileList, setFileList] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [rawApiResult, setRawApiResult] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [vendorData, setVendorData] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetchBranches = async () => {
      try {
        setIsLoadingBranches(true);
        const response = await globalRequest(Apis.ORDERS.GET_ALL_BRANCH_FOR_BULK, "get", {}, {}, true);
        const branchList = response?.data?.data?.data || [];
        setBranches(branchList);
      } catch (error) {
        toast.error(t("unableToLoadBranches") + (error?.message || ""));
      } finally {
        setIsLoadingBranches(false);
      }
    };
    fetchBranches();
  }, [isOpen]);

  // ─── Preview columns ────────────────────────────────────────────────────────
  const previewColumns = [
    {
      title: t("firstName"),
      dataIndex: "firstName",
      key: "firstName",
      width: 100,
      render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.firstName?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ),
    },
    {
      title: t("lastName"),
      dataIndex: "lastName",
      key: "lastName",
      width: 100,
      render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.lastName?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ),
    },
    {
      title: t("email"),
      dataIndex: "email",
      key: "email",
      width: 180,
      render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.email?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ),
    },
    {
      title: t("mobile"),
      dataIndex: "mobileNo",
      key: "mobileNo",
      width: 120,
      render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.mobileNo?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ),
    },
    { title: t("packagingId"), dataIndex: "packaging_type_id", key: "packaging_type_id", width: 100 },
    { title: t("itemSensitivity"), dataIndex: "item_sensitivity_id", key: "item_sensitivity_id", width: 100 },
    {
      title: t("locationLink"),
      dataIndex: "maps_link",
      key: "maps_link",
      width: 220,
      render: (text, row) => (
        <div>
          <div
            style={{
              maxWidth: 210,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
              color: "#1890ff",
            }}
            title={text || ""}
          >
            {text ? (
              <a href={text} target="_blank" rel="noopener noreferrer">{text}</a>
            ) : "-"}
          </div>
          {row.errors?.maps_link?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ),
    },
    {
      title: t("externalOrderNumber"),
      dataIndex: "external_order_number",
      key: "external_order_number",
      width: 150,
      render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.external_order_number?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ),
    },
    {
      title: t("deliveryDate"),
      dataIndex: "delivery_date",
      key: "delivery_date",
      width: 130,
      render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.delivery_date?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ),
    },
    { title: t("timeSlot"), dataIndex: "delivery_time_slot_id", key: "delivery_time_slot_id", width: 80 },
  ];

  // ─── Result columns (after upload) with DEDUCTION DETAILS ────────────────────
  const resultColumns = [
    {
      title: t("status"),
      dataIndex: "success",
      key: "status",
      width: 90,
      fixed: "left",
      render: (success) =>
        success ? (
          <Tag icon={<CheckCircleOutlined />} color="success">{t("success")}</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">{t("failed")}</Tag>
        ),
    },
    {
      title: t("orderNumber"),
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 140,
      render: (text) => text || "-",
    },
    {
      title: t("customerName"),
      key: "customerName",
      width: 140,
      render: (_, r) => r.customerName || "-",
    },
    { title: t("mobile"), dataIndex: "mobileNo", key: "mobileNo", width: 120 },
    { title: t("email"), dataIndex: "email", key: "email", width: 160, render: (t) => t || "-" },
    { title: t("branchName"), dataIndex: "branchName", key: "branchName", width: 150 },
    { title: t("externalOrderNo"), dataIndex: "externalOrderNumber", key: "externalOrderNumber", width: 160, render: (t) => t || "-" },
    { title: t("deliveryDate"), dataIndex: "deliveryDate", key: "deliveryDate", width: 120, render: (t) => t || "-" },
    { title: t("timeSlot"), dataIndex: "timeSlot", key: "timeSlot", width: 160, render: (t) => t || "-" },
    { title: t("packaging"), dataIndex: "packagingType", key: "packagingType", width: 120, render: (t) => t || "-" },
    { title: t("sensitivity"), dataIndex: "sensitivity", key: "sensitivity", width: 110, render: (t) => t || "-" },
    {
      title: t("address"),
      dataIndex: "address",
      key: "address",
      width: 220,
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <span style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
              {val}
            </span>
          </Tooltip>
        ) : "-",
    },
    { title: t("buildingNo"), dataIndex: "buildingNumber", key: "buildingNumber", width: 110, render: (val) => val || "-" },
    {
      title: t("latLng"),
      key: "latLng",
      width: 160,
      render: (_, r) => r.lat && r.lng ? `${r.lat}, ${r.lng}` : "-",
    },
    {
      title: t("distanceKM"),
      dataIndex: "distanceKM",
      key: "distanceKM",
      width: 120,
      render: (val) => val ? Number(val).toFixed(2) : "-",
    },
    {
      title: t("baseDeliveryFee"),
      dataIndex: "baseDeliveryFee",
      key: "baseDeliveryFee",
      width: 140,
      render: (val) => val ? `SAR ${Number(val).toFixed(2)}` : "-",
    },
    {
      title: t("actualDeliveryFee"),
      dataIndex: "deliveryFee",
      key: "deliveryFee",
      width: 140,
      render: (val) => val ? `SAR ${Number(val).toFixed(2)}` : "-",
    },
    {
      title: t("vendorDeduction"),
      dataIndex: "vendor_deduction",
      key: "vendor_deduction",
      width: 140,
      render: (val) =>
        val && Number(val) > 0 ? (
          <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
            SAR {Number(val).toFixed(2)}
          </span>
        ) : "-",
    },
    {
      title: t("deductionDetails"),
      dataIndex: "deductionDetails",
      key: "deductionDetails",
      width: 250,
      render: (details) => {
        if (!details) return "-";
        return (
          <Collapse
            size="small"
            items={[{
              key: "1",
              label: t("flatcapDeduction"),
              children: (
                <div style={{ fontSize: 12 }}>
                  <div><strong>{t("reason")}:</strong> {details.reason}</div>
                  <div><strong>{t("type")}:</strong> {details.deductionType}</div>
                  <div><strong>{t("baseFee")}:</strong> SAR {Number(details.baseDeliveryFee).toFixed(2)}</div>
                  <div><strong>{t("flatcapAmount")}:</strong> SAR {Number(details.flatCapAmount).toFixed(2)}</div>
                  <div><strong>{t("deductionAmount")}:</strong> SAR {Number(details.vendor_deduction).toFixed(2)}</div>
                  <div><strong>{t("appliedAt")}:</strong> {new Date(details.appliedAt).toLocaleString()}</div>
                </div>
              ),
            }]}
          />
        );
      },
    },
    {
      title: t("errorDetails"),
      dataIndex: "errorDetails",
      key: "errorDetails",
      width: 220,
      render: (errors) => {
        if (!errors || errors.length === 0) return "-";
        return (
          <Collapse
            size="small"
            items={[{
              key: "1",
              label: `${errors.length} ${t("errors")}`,
              children: (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {errors.map((err, idx) => (
                    <li key={idx} style={{ color: "#ff4d4f" }}>{err}</li>
                  ))}
                </ul>
              ),
            }]}
          />
        );
      },
    },
  ];

  // ─── Export successful orders to Excel ─────────────────────────────────────
  const handleExport = () => {
    if (!rawApiResult?.success?.length) {
      toast.error(t("noSuccessfulOrdersToExport"));
      return;
    }

    const rows = rawApiResult.success.map((item, idx) => {
      const o = item.orderRow || {};
      const deductionDetails = item.deductionDetails || {};
      return {
        "S.No":                    idx + 1,
        "Order Number":            item.order?.data?.orderNumber || "",
        "Customer First Name":     o.firstName        || "",
        "Customer Last Name":      o.lastName         || "",
        "Customer Full Name":      `${o.firstName || ""} ${o.lastName || ""}`.trim(),
        "Mobile No":               o.mobileNo         || "",
        "Email":                   o.email            || "",
        "Branch Name":             o.branchName || o.branch_name || "",
        "Branch ID":               o.branchId   || o.branch_id   || "",
        "External Order Number":   o.external_order_number || "",
        "Delivery Date":           o.delivery_date    || "",
        "Delivery Type":           o.delivery_type    || "",
        "Time Slot":               o.time_slot_value  || "",
        "Packaging Type":          o.packaging_type_name || "",
        "Packaging Type ID":       o.packaging_type_id   || "",
        "Item Sensitivity":        o.item_sensitivity_name || o.sensitivity_name || "",
        "Item Sensitivity ID":     o.item_sensitivity_id  || "",
        "Prep Time (min)":         o.prep_time        || "",
        "Latitude":                o.lat              || "",
        "Longitude":               o.lng              || "",
        "Building Number":         o.buildingN || o.buildingNumber || "",
        "Address":                 o.address          || "",
        "Time Slot ID (Mapped)":   item.mappedIds?.time_slot_id     || "",
        "Packaging ID (Mapped)":   item.mappedIds?.packaging_type_id || "",
        "Sensitivity ID (Mapped)": item.mappedIds?.item_sensitivity_id || "",
        "Distance KM":             item.distanceKM || "",
        "Base Delivery Fee":       item.baseDeliveryFee || "",
        "Actual Delivery Fee":     item.deliveryFee || "",
        "Vendor Deduction":        item.vendor_deduction || "",
        "Deduction Reason":        deductionDetails.reason || "",
        "Deduction Type":          deductionDetails.deductionType || "",
        "Flatcap Amount":          deductionDetails.flatCapAmount || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto column width
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String(r[key] || "").length)) + 2,
    }));
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Orders");

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `bulk_orders_${dateStr}.xlsx`);
    toast.success(t("exportSuccessful"));
  };

  // ─── Branch selection ───────────────────────────────────────────────────────
  const handleBranchChange = async (branchId) => {
    setSelectedBranchId(branchId);
    const br = branches.find((b) => b.id === branchId);
    setSelectedBranchName(br?.name || "");
    setSelectedVendorName("");
    setVendorData(null);
    if (!branchId) return;
    try {
      const branchResponse = await globalRequest(Apis.MANAGE_VENDOR.BRANCH_DETAIL + "/" + branchId, "get", {}, {}, true);
      const branchData = branchResponse?.data?.data;
      if (branchData?.userId) {
        const vendorResponse = await globalRequest(Apis.MANAGE_VENDOR.GET_VENDOR_DETAIL + "/" + branchData.userId, "get", {}, {}, true);
        const vd = vendorResponse?.data?.data;
        if (vd) {
          setSelectedVendorName(`${vd.firstName || ""} ${vd.lastName || ""}`.trim());
          setVendorData(vd);
        }
      }
    } catch (error) {
      toast.error(t("unableToFetchBranchVendor") + (error?.message || ""));
    }
  };

  // ─── Reliable mobile number parser ─────────────────────────────────────────
  const parseMobileNo = (val) => {
    if (val === null || val === undefined || val === "") return "";
    if (typeof val === "number" && Number.isInteger(val)) return String(val);
    if (typeof val === "number") return String(Math.round(val));
    const str = String(val).trim();
    if (/^-?\d+(\.\d+)?[eE][+-]?\d+$/.test(str)) {
      const num = Number(str);
      return Number.isInteger(num) ? String(num) : String(Math.round(num));
    }
    return str.replace(/[^\d+]/g, "").replace(/^\+/, "");
  };

  const normalizeDate = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    const dashMatch = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) return `${dashMatch[3]}-${dashMatch[2]}-${dashMatch[1]}`;
    const slashMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) return `${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`;
    return s;
  };

  // ─── File parsing ───────────────────────────────────────────────────────────
  const handleBeforeUpload = (file) => {
    setFileList([file]);
    setIsUploading(true);
    setUploadResults(null);
    setRawApiResult(null);

    const isCsv = file.name.endsWith(".csv");

    const processData = async (rawData) => {
      try {
        const processedData = rawData.map((row, index) => {
          const mobileNo = parseMobileNo(row.mobileNo ?? row.mobileno ?? row.mobile ?? "");
          const maps_link = String(
            row.maps_link ||
            row.mapsLink ||
            row.location_link ||
            row["Location Link"] ||
            row["Address Link"] ||
            row["maps link"] ||
            row["address_link"] ||
            ""
          ).trim();
          return { ...row, mobileNo, maps_link, key: index + 2, errors: {} };
        });

        const validatedData = [];
        for (const row of processedData) {
          const errors = {};
          const getString = (value) => String(value ?? "").trim();
          const firstName = getString(row.firstName || row.firstname || row.first_name);
          const lastName = getString(row.lastName || row.lastname || row.last_name);
          const email = getString(row.email ?? row.Email ?? "");
          const externalOrderNumber = getString(row.external_order_number || row.externalOrderNumber || row["External Order Number"] || "");
          const mapsLink = getString(
            row.maps_link ||
            row.mapsLink ||
            row.location_link ||
            row.address_link ||
            row.addressLink ||
            row["Location Link"] ||
            row["Address Link"] ||
            row["location_link"] ||
            row["address_link"] ||
            row["maps link"] ||
            ""
          );
          const deliveryDate = getString(row.delivery_date || row.deliveryDate || row["Delivery Date"] || "");

          if (!firstName) errors.firstName = ["First name is required"];
          if (!lastName) errors.lastName = ["Last name is required"];
          if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = ["Invalid email format"];
          }
          if (!externalOrderNumber) {
            errors.external_order_number = ["External order number is required"];
          } else if (externalOrderNumber.length < 5) {
            errors.external_order_number = ["External order number must be at least 5 characters"];
          } else if (externalOrderNumber.length > 14) {
            errors.external_order_number = ["External order number must not exceed 14 characters"];
          }
          if (!row.mobileNo) {
            errors.mobileNo = ["Mobile number is required"];
          } else if (!/^\d{9}$/.test(row.mobileNo.replace(/\D/g, ""))) {
            errors.mobileNo = ["Mobile number must be 9 digits"];
          }
          if (deliveryDate && !/^(\d{2}[-\/]\d{2}[-\/]\d{4}|\d{4}-\d{2}-\d{2})$/.test(deliveryDate)) {
            errors.delivery_date = ["Invalid date format"];
          } else if (deliveryDate) {
            let parsedDate;
            if (deliveryDate.includes("-") || deliveryDate.includes("/")) {
              const parts = deliveryDate.split(/[-\/]/);
              if (parts.length === 3) {
                parsedDate = parts[2].length === 4
                  ? new Date(parts[2], parts[1] - 1, parts[0])
                  : new Date(parts[0], parts[1] - 1, parts[2]);
              }
            } else {
              parsedDate = new Date(deliveryDate);
            }
            if (parsedDate && !isNaN(parsedDate.getTime())) {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              parsedDate.setHours(0, 0, 0, 0);
              if (parsedDate < today) {
                errors.delivery_date = ["Delivery date must be today or future date"];
              }
            } else {
              errors.delivery_date = ["Invalid date format"];
            }
          }
          if (!mapsLink) {
            errors.maps_link = ["Google Maps link is required"];
          } else if (!/maps|google|goo\.gl/.test(mapsLink.toLowerCase())) {
            errors.maps_link = ["Invalid Google Maps link"];
          }

          validatedData.push({
            ...row,
            email,
            external_order_number: externalOrderNumber,
            delivery_date: deliveryDate,
            maps_link: mapsLink,
            errors,
          });
        }

        setParsedData(validatedData);
        setIsUploading(false);
      } catch (error) {
        toast.error(t("errorProcessingData") + error.message);
        setIsUploading(false);
      }
    };

    if (isCsv) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (results) => processData(results.data),
        error: (error) => {
          toast.error(t("errorParsingCsv") + error.message);
          setIsUploading(false);
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "array", raw: true, cellDates: false });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
          processData(json);
        } catch (error) {
          toast.error(t("errorParsingExcel") + error.message);
          setIsUploading(false);
        }
      };
      reader.onerror = () => {
        toast.error(t("errorReadingFile"));
        setIsUploading(false);
      };
      reader.readAsArrayBuffer(file);
    }
    return false;
  };

  const handleRemove = () => {
    setFileList([]);
    setParsedData([]);
    setUploadResults(null);
    setRawApiResult(null);
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedBranchId) { toast.error(t("selectBranchBeforeUpload")); return; }
    if (!vendorData) { toast.error(t("vendorDetailsNotLoaded")); return; }
    if (!vendorData?.vendorDetail?.settings?.walletToggle) { toast.error(t("prepaidWalletNotEnabled")); return; }
    if (!(vendorData?.vendorDetail?.walletBalance > 0)) { toast.error(t("vendorWalletMustBePositive")); return; }
    if (parsedData.length === 0) { toast.error(t("noDataToUpload")); return; }
    if (parsedData.some((row) => row.errors && Object.keys(row.errors).length > 0)) return;

    const ordersData = parsedData.map((row) => {
      const mobileNo = parseMobileNo(row.mobileNo ?? row.mobileno ?? row.mobile ?? "0000000000") || "0000000000";

      const rawPkg = row.packaging_type_id || row.packagingTypeId || row.packagingId || row.packaging_type_name || row.packagingName || row.packaging || "";
      const rawSens = row.item_sensitivity_id || row.sensitivityId || row.sensitivity_id || row.item_sensitivity_name || row.sensitivityName || row.sensitivity || "";
      const rawSlot = row.delivery_time_slot_id || row.timeSlotId || row.time_slot_id || row.time_slot_value || "";

      const isNumericPkg = rawPkg && /^\d+$/.test(String(rawPkg).trim());
      const isNumericSens = rawSens && /^\d+$/.test(String(rawSens).trim());
      const isNumericSlot = rawSlot && /^\d+$/.test(String(rawSlot).trim());

      const firstName = String(row.firstName || row.firstname || row.first_name || "Bulk").trim();
      const lastName = String(row.lastName || row.lastname || row.last_name || "Customer").trim();
      const deliveryDate = normalizeDate(row.delivery_date || row.deliveryDate || row.delivery_da);
      const buildingN = String(row.buildingN || row.buildingNumber || row.building_number || "").trim();

      return {
        branch_id: selectedBranchId,
        branch_name: selectedBranchName || null,
        firstName, lastName, mobileNo,
        email: String(row.email || "").trim(),
        delivery_type: String(row.delivery_type || row.deliveryType || "schedule").trim(),
        delivery_date: deliveryDate,
        delivery_time_slot_id: isNumericSlot ? parseInt(rawSlot) : null,
        time_slot_value: !isNumericSlot ? (String(rawSlot).trim() || null) : null,
        maps_link: String(
          row.maps_link || row.mapsLink || row.location_link ||
          row.address_link || row.addressLink ||
          row["Location Link"] || row["Address Link"] ||
          row["location_link"] || row["address_link"] || row["maps link"] || ""
        ).trim(),
        packaging_type_id: isNumericPkg ? parseInt(rawPkg) : null,
        packaging_type_name: !isNumericPkg ? (String(rawPkg).trim() || null) : (row.packaging_type_name || null),
        item_sensitivity_id: isNumericSens ? parseInt(rawSens) : null,
        item_sensitivity_name: !isNumericSens ? (String(rawSens).trim() || null) : (row.item_sensitivity_name || null),
        prep_time: String(row.prep_time || row.prepTime || "0").trim(),
        custom_product_price: parseFloat(row.custom_product_price || row.price || 0) || 0,
        reference_link: String(row.reference_link || "").trim(),
        external_order_number: String(row.external_order_number || row.externalOrderNumber || "").trim(),
        customer_details: { firstName, lastName, mobileNo, email: String(row.email || "").trim(), branch_id: selectedBranchId, branch_name: selectedBranchName },
        delivery_details: { delivery_type: String(row.delivery_type || "schedule").trim(), delivery_date: deliveryDate, delivery_time_slot_id: isNumericSlot ? parseInt(rawSlot) : null },
      };
    });

    const response = await globalRequest(Apis.ORDERS.BULK_UPLOAD, "post", { orders: ordersData }, {}, true);

    if (response?.status === 200 || response?.data?.status === "SUCCESS") {
      const result = response.data.data || {};
      setRawApiResult(result);

      const successRows = (result.success || []).map((item, idx) => {
        const o = item.orderRow || {};
        return {
          key: `success-${idx}`,
          rowNumber: idx + 1,
          success: true,
          orderNumber: item.order?.data?.orderNumber || "",
          customerName: `${o.firstName || ""} ${o.lastName || ""}`.trim(),
          mobileNo: o.mobileNo || "",
          email: o.email || "",
          branchName: o.branchName || o.branch_name || "",
          externalOrderNumber: o.external_order_number || "",
          deliveryDate: o.delivery_date || "",
          timeSlot: o.time_slot_value || "",
          packagingType: o.packaging_type_name || "",
          sensitivity: o.item_sensitivity_name || o.sensitivity_name || "",
          address: o.address || "",
          buildingNumber: o.buildingN || o.buildingNumber || "",
          lat: o.lat || "",
          lng: o.lng || "",
          distanceKM: item.distanceKM || 0,
          baseDeliveryFee: item.baseDeliveryFee || 0,
          deliveryFee: item.deliveryFee || 0,
          vendor_deduction: item.vendor_deduction || 0,
          deductionDetails: item.deductionDetails || null,
          errorDetails: [],
        };
      });

      const errorRows = (result.errors || []).map((item, idx) => ({
        key: `error-${idx}`,
        rowNumber: successRows.length + idx + 1,
        success: false,
        customerName: `${item.row?.firstName || ""} ${item.row?.lastName || ""}`.trim(),
        mobileNo: item.row?.mobileNo || "",
        email: item.row?.email || "",
        branchName: item.row?.branch_name || item.row?.branchName || "",
        externalOrderNumber: item.row?.external_order_number || "",
        deliveryDate: item.row?.delivery_date || "",
        timeSlot: item.row?.time_slot_value || "",
        packagingType: item.row?.packaging_type_name || "",
        sensitivity: item.row?.item_sensitivity_name || "",
        address: item.row?.address || "",
        buildingNumber: item.row?.buildingN || item.row?.buildingNumber || "",
        lat: item.row?.lat || "",
        lng: item.row?.lng || "",
        distanceKM: 0,
        baseDeliveryFee: 0,
        deliveryFee: 0,
        vendor_deduction: 0,
        deductionDetails: null,
        errorDetails: [item.error || "Unknown error"],
      }));

      setUploadResults([...successRows, ...errorRows]);

      const successCount = result.success?.length ?? 0;
      const failureCount = result.errors?.length ?? 0;

      if (failureCount === 0) {
        toast.success(`✓ ${t("allOrdersCreatedSuccessfully").replace("All", `All ${successCount}`)}`);
        if (reloadPage) reloadPage();
      } else {
        toast.error(`✗ ${failureCount} ${t("ordersFailed")}`);
      }
    } else {
      toast.error(response?.data?.message || t("bulkUploadFailed"));
    }
  };

  // ─── Close & reset ──────────────────────────────────────────────────────────
  const handleClose = () => {
    setFileList([]);
    setParsedData([]);
    setUploadResults(null);
    setRawApiResult(null);
    setSelectedBranchId(null);
    setSelectedBranchName("");
    setSelectedVendorName("");
    setVendorData(null);
    onClose();
  };

  const successCount = uploadResults ? uploadResults.filter((r) => r.success).length : 0;
  const failureCount = uploadResults ? uploadResults.filter((r) => !r.success).length : 0;
  const hasParseErrors = parsedData.some((row) => row.errors && Object.keys(row.errors).length > 0);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={t("uploadBulkOrders")}
      open={isOpen}
      onCancel={handleClose}
      width={1400}
      destroyOnClose
      footer={
        uploadResults ? (
          [
            <Button
              key="export"
              type="primary"
              icon={<DownloadOutlined />}
              disabled={successCount === 0}
              onClick={handleExport}
              style={{ backgroundColor: "#37846B", borderColor: "#37846B" }}
            >
              {t("exportSuccessfulOrders")} ({successCount})
            </Button>,
            <Button key="close" onClick={handleClose}>
              {t("close")}
            </Button>,
          ]
        ) : (
          [
            <Button key="cancel" onClick={handleClose}>
              {t("cancel")}
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={isUploading}
              disabled={parsedData.length === 0}
              onClick={handleSubmit}
            >
              {t("submit")}
            </Button>,
          ]
        )
      }
    >
      {!uploadResults ? (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-around" }}>
              <Select
                placeholder={t("selectBranch")}
                value={selectedBranchId}
                onChange={handleBranchChange}
                loading={isLoadingBranches}
                style={{ minWidth: 250 }}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
              <Upload
                accept=".csv,.xlsx"
                fileList={fileList}
                beforeUpload={handleBeforeUpload}
                onRemove={handleRemove}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} style={{ width: 250 }}>
                  {t("selectDataFile")}
                </Button>
              </Upload>
            </div>
            <div><strong>{t("selectedBranch")}:</strong> {selectedBranchName || "-"}</div>
            <div><strong>{t("vendorName")}:</strong>    {selectedVendorName || "-"}</div>
          </div>

          {hasParseErrors && (
            <div style={{ color: "#ff4d4f", marginBottom: 12 }}>
              {t("fixRowErrors")}
            </div>
          )}

          {parsedData.length > 0 && (
            <>
              <h3 style={{ marginTop: 20, marginBottom: 10 }}>
                {t("previewData")} ({parsedData.length} {t("previewRows")})
              </h3>
              <Table
                dataSource={parsedData}
                columns={previewColumns}
                size="small"
                pagination={{ pageSize: 5 }}
                scroll={{ x: 2000, y: 300 }}
              />
            </>
          )}
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 16,
              padding: "12px 16px",
              borderRadius: 8,
              background: failureCount === 0 ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${failureCount === 0 ? "#b7eb8f" : "#ffccc7"}`,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>{t("totalOrders")}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{uploadResults.length}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>{t("successful")}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}>{successCount}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>{t("failed")}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: failureCount > 0 ? "#ff4d4f" : "#888" }}>
                {failureCount}
              </div>
            </div>
            {selectedBranchName && (
              <div style={{ flex: 2 }}>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>{t("branchName")}</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{selectedBranchName}</div>
                {selectedVendorName && (
                  <div style={{ fontSize: 13, color: "#555" }}>{t("vendorName")}: {selectedVendorName}</div>
                )}
              </div>
            )}
          </div>

          <Table
            dataSource={uploadResults}
            columns={resultColumns}
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("totalRows")} ${total}`,
            }}
            scroll={{ x: 2200 }}
            rowKey="key"
            rowClassName={(r) => (r.success ? "" : "bulk-upload-error-row")}
          />

          <style>{`
            .bulk-upload-error-row td { background: #fff2f0 !important; }
          `}</style>
        </>
      )}
    </Modal>
  );
};

export default BulkUploadModal;